import {
    addDays,
    addMinutes,
    differenceInMinutes,
    format,
    formatISO,
    min,
    parse,
    parseISO,
    startOfToday,
} from 'date-fns';
import {
    AlignLeftIcon,
    CalendarIcon,
    ClockIcon,
    MapPinIcon,
    MinusIcon,
    StoreIcon,
    TypeIcon,
    UserCircleIcon,
    XIcon,
} from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { Button } from '../../../components/ui/button.tsx';
import { DatePicker } from '../../../components/ui/date-picker.tsx';
import { Input } from '../../../components/ui/input.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { TimePicker } from '../../../components/ui/time-picker.tsx';
import { Switch } from '../../../components/ui/toggle.tsx';
import { getFullAddressString } from '../../../lib/address-helper.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import type { EventInput } from '../../../server/queries/utils/crm-event-build-input.ts';
import { getDefaultRecurrence } from '../../../utils/rrule.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { RichTextInput } from '../../rich-text/components/rich-text-input.tsx';
import { CustomerSelect } from '../../shared/components/form-items-with-fetch/customer-select.tsx';
import { EventAddressSelect } from './event-address-select.tsx';
import { EventAttendeesField } from './event-attendees-field.tsx';
import { RRuleInput } from './rrule-input.tsx';

export type EventFormData = EventInput;

export type EventFormFieldsProps = {
    form: UseFormReturn<EventFormData, undefined>;
    customer?: CustomerDTO;
    ownerName: string;
    eventId?: string;
};

const DEFAULT_EVENT_LENGTH_MINUTES = 30;

const parseTime = (day: Date, time: string | undefined | null) => {
    return time ? parse(time, 'HH:mm:ss', day) : undefined;
};

const formatTime = (date: Date) => {
    return format(date, 'HH:mm:ss');
};

const getMinEndTime = (startTime: string, referenceDate: Date) => {
    const start = parse(startTime, 'HH:mm:ss', referenceDate);
    return formatTime(addMinutes(start, 15));
};

export const EventFormFields = ({ form, customer, ownerName, eventId }: EventFormFieldsProps) => {
    const { t } = useLocale('events');
    const {
        control,
        formState: { errors, disabled },
        setValue,
        watch,
        getValues,
    } = form;

    const date = watch('date');
    const currentDay = date ? parseISO(date) : startOfToday();
    const startTime = watch('start_time');

    const customerId = useWatch({ control, name: 'customer_id' });

    return (
        <DialogForm.ItemList>
            {/* Customer */}
            <DialogForm.Item className="items-start gap-0 flex-nowrap">
                <DialogForm.Label htmlFor="customer_id" icon={StoreIcon} />
                <DialogForm.Body className="min-w-0 flex-col">
                    <Controller
                        name="customer_id"
                        control={control}
                        render={({ field: { value, onChange, disabled } }) => (
                            <CustomerSelect
                                className="w-full"
                                variant="inline"
                                inputPlaceholder={t('events__form_customer_placeholder')}
                                value={value}
                                onChange={(customerId, customer) => {
                                    onChange(customerId);
                                    setValue('address', getFullAddressString(customer, customer.name), {
                                        shouldValidate: true,
                                    });
                                }}
                                onClear={() => {
                                    onChange('');
                                    setValue('address', '', { shouldValidate: true });
                                }}
                                customer={value === customer?.customerId ? customer : undefined}
                                error={!!errors.customer_id}
                                disabled={disabled}
                                tracking={{
                                    context: 'event_customer_select',
                                    context_entity_type: 'event',
                                    context_entity_id: eventId,
                                }}
                            />
                        )}
                    />
                    <DialogForm.Error error={errors.customer_id} />
                </DialogForm.Body>
            </DialogForm.Item>

            {/* Title */}
            <DialogForm.Item className="gap-1">
                <DialogForm.Label htmlFor="title" icon={TypeIcon} />
                <DialogForm.Body>
                    <Controller
                        control={control}
                        name="title"
                        rules={{
                            required: {
                                value: true,
                                message: t('form__text_required', { ns: 'common' }),
                            },
                        }}
                        render={({ field }) => (
                            <Input
                                variant="inline"
                                placeholder={t('events__form_title_placeholder')}
                                maxLength={100}
                                {...field}
                                value={field.value}
                            />
                        )}
                    />
                </DialogForm.Body>
                <DialogForm.Error error={errors.title} />
            </DialogForm.Item>

            {/* Address */}
            <DialogForm.Item className="items-start gap-1">
                <DialogForm.Label icon={MapPinIcon} />
                <DialogForm.Body className="min-w-0">
                    <Controller
                        name="address"
                        control={control}
                        render={({ field }) => (
                            <EventAddressSelect
                                customerId={customerId}
                                value={field.value}
                                onChange={field.onChange}
                                disabled={field.disabled}
                            />
                        )}
                    />
                </DialogForm.Body>
                <DialogForm.Error error={errors.address} />
            </DialogForm.Item>

            {/* Event Date */}
            <DialogForm.Item className="items-start gap-0">
                <DialogForm.Label icon={CalendarIcon} />
                <DialogForm.Body className="flex-col gap-2">
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2 flex-col sm:flex-row sm:items-center">
                            <Controller
                                name="date"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        disableIcon
                                        mode="single"
                                        value={field.value ? new Date(field.value) : new Date()}
                                        valuePrefix=""
                                        onChange={(value) => {
                                            field.onChange(
                                                formatISO(value ?? new Date(), {
                                                    representation: 'date',
                                                }),
                                            );
                                        }}
                                        disabled={field.disabled}
                                    />
                                )}
                            />
                            <Controller
                                name="recurrence"
                                control={control}
                                render={({ field }) => (
                                    <div className="px-3">
                                        <Switch
                                            checked={!!field.value}
                                            onCheckedChange={(value) => {
                                                field.onChange(value ? getDefaultRecurrence() : '');
                                            }}
                                            size="sm"
                                            label={t('events__form_regular_event_label')}
                                            disabled={field.disabled}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                        <Controller
                            name="recurrence"
                            control={control}
                            render={({ field }) => (
                                <>
                                    {!!field.value && (
                                        <div className="pl-3">
                                            <RRuleInput startDate={watch('date')} {...field} />
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    </div>
                </DialogForm.Body>
                <DialogForm.Error error={errors.date} />
            </DialogForm.Item>

            {/* Time Range */}
            <DialogForm.Item className="items-center flex-nowrap gap-0">
                <DialogForm.Label icon={ClockIcon} />
                <DialogForm.Body className="flex-row items-center gap-1">
                    <Controller
                        name="start_time"
                        control={control}
                        render={({ field }) => (
                            <TimePicker
                                value={field.value ?? null}
                                onChange={(value) => {
                                    const currentDay = parseISO(getValues('date'));
                                    const nextDay = addDays(currentDay, 1);
                                    const startTime = parseTime(currentDay, field.value);
                                    const endTime = parseTime(
                                        getValues('end_time') === '00:00:00' ? nextDay : currentDay,
                                        getValues('end_time'),
                                    );

                                    field.onChange(value);

                                    const nextStartTime = parseTime(currentDay, value);

                                    if (!nextStartTime) {
                                        setValue('end_time', undefined);
                                        return;
                                    }

                                    const eventLength =
                                        startTime && endTime
                                            ? differenceInMinutes(endTime, startTime)
                                            : DEFAULT_EVENT_LENGTH_MINUTES;
                                    const nextEndTime = min([
                                        addMinutes(nextStartTime, eventLength),
                                        nextDay,
                                    ]);

                                    setValue('end_time', formatTime(nextEndTime));
                                }}
                                placeholder={t('events__form_time_start_placeholder')}
                                disabled={field.disabled}
                            />
                        )}
                    />
                    <MinusIcon className="text-sm text-neutral-400" />
                    <Controller
                        name="end_time"
                        control={control}
                        render={({ field }) => (
                            <TimePicker
                                disabled={field.disabled || !startTime}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                placeholder={t('events__form_time_end_placeholder')}
                                min={startTime ? getMinEndTime(startTime, currentDay) : undefined}
                            />
                        )}
                    />
                    {!!startTime && !disabled && (
                        <Button
                            variant="ghost"
                            icon={XIcon}
                            size="icon"
                            className="p-0"
                            onClick={() => {
                                form.setValue('end_time', undefined);
                                form.setValue('start_time', undefined);
                            }}
                        />
                    )}
                </DialogForm.Body>
                <DialogForm.Error error={errors.start_time || errors.end_time} />
            </DialogForm.Item>

            <EventAttendeesField control={control} setValue={setValue} disabled={disabled} />

            <DialogForm.Item className="items-start flex-nowrap">
                <DialogForm.Label htmlFor="agenda" icon={AlignLeftIcon} />
                <DialogForm.Body className="min-w-0">
                    <Controller
                        control={control}
                        name="agenda"
                        render={({ field }) => (
                            <RichTextInput
                                placeholder={t('events__form_agenda_placeholder')}
                                value={field.value ?? null}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </DialogForm.Body>
                <DialogForm.Error error={errors.agenda} />
            </DialogForm.Item>

            {/* Created By (Display Only) */}
            <DialogForm.Item>
                <DialogForm.Label icon={UserCircleIcon} />
                <DialogForm.Body className="items-center">
                    <Text color="neutral-950">{t('events__form_created_by', { ownerName })}</Text>
                </DialogForm.Body>
            </DialogForm.Item>
        </DialogForm.ItemList>
    );
};
